import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import LinearProgress from "@mui/material/LinearProgress";
import getStyled from "../../hooks/getStyled";
import SFTypography from "../typography";
import { useTranslation } from "react-i18next";

import { Colors } from "../../constants/colors";
const COLORS = Object.keys(Colors).map((key) => key.toLowerCase());

const { styled } = getStyled();

const StyledProgressBar = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== "backgroundColor",
})(({ theme, backgroundColor }) => ({
  backgroundColor:
    backgroundColor === "transparent"
      ? "transparent"
      : theme.palette[backgroundColor].main,
  height: "6px",
}));

const StyledTextWrapper = styled("div")(({ theme }) => ({
  paddingBottom: theme.spacing(1),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const StyledProgressBarWrapper = styled("div")(({ theme }) => ({
  width: "100%",
}));

const SFProgressBar = ({
  progress,
  loadingText,
  variant,
  failed,
  color = "green300",
  backgroundColor = "grey200",
  className,
}) => {
  const { t } = useTranslation();
  const [innerProgress, setProgress] = useState(0);

  useEffect(() => {
    setProgress(progress);
  }, [progress]);

  return (
    <StyledProgressBarWrapper>
      {loadingText && (
        <StyledTextWrapper>
          {loadingText}
          {variant === "determinate" && !failed && (
            <SFTypography variant="body1bold" component="span">
              {`${t("components.progressBar.percentage", {
                val: innerProgress,
              })} ${t("components.progressBar.completed")}`}
            </SFTypography>
          )}
          {failed && (
            <SFTypography variant="body1bold" component="span">
              {t("components.progressBar.failed")}
            </SFTypography>
          )}
        </StyledTextWrapper>
      )}
      <StyledProgressBar
        color={failed ? "red300" : color}
        backgroundColor={backgroundColor}
        variant={failed ? "determinate" : variant}
        value={failed ? 100 : innerProgress}
        className={className}
      />
    </StyledProgressBarWrapper>
  );
};

SFProgressBar.propTypes = {
  progress: PropTypes.number,
  variant: PropTypes.oneOf(["indeterminate", "determinate"]),
  loadingText: PropTypes.node,
  failed: PropTypes.bool,
  color: PropTypes.oneOf(COLORS),
  backgroundColor: PropTypes.oneOf([...COLORS, "transparent"]),
  className: PropTypes.string,
};

export default SFProgressBar;
